import { useState } from 'react';
import { useProductCostHistory, useProductCostStatistics } from '@/hooks/useCostHistory';
import { TrendingUp, TrendingDown, Minus, Calendar, User, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { formatPrice } from '@/utils/format';
import clsx from 'clsx';

interface CostHistoryPanelProps {
  productId: number;
  productName: string;
}

export default function CostHistoryPanel({ productId, productName: _productName }: CostHistoryPanelProps) {
  void _productName; // Reservado para uso futuro
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: history, isLoading: loadingHistory } = useProductCostHistory(productId);
  const { data: stats, isLoading: loadingStats } = useProductCostStatistics(productId);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getChangeIcon = (percent?: number | string | null) => {
    const num = percent != null ? Number(percent) : null;
    if (!num || num === 0) return <Minus className="h-4 w-4 text-gray-400" />;
    if (num > 0) return <TrendingUp className="h-4 w-4 text-red-500" />;
    return <TrendingDown className="h-4 w-4 text-green-500" />;
  };

  const getChangeColor = (percent?: number | string | null) => {
    const num = percent != null ? Number(percent) : null;
    if (!num || num === 0) return 'text-gray-500';
    if (num > 0) return 'text-red-600';
    return 'text-green-600';
  };

  if (loadingHistory || loadingStats) {
    return (
      <div className="bg-white border rounded-lg p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="bg-gray-50 border border-dashed rounded-lg p-4 text-center text-gray-500">
        <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">Sin historial de costos registrado</p>
        <p className="text-xs">El historial comenzará cuando se actualice el costo</p>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      {/* Header con estadísticas resumidas */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-500" />
          <span className="font-medium">Historial de Costos</span>
          <span className="text-sm text-gray-500">({history.length} registros)</span>
        </div>
        <div className="flex items-center gap-4">
          {stats && stats.total_change_percent != null && (
            <div className={clsx('flex items-center gap-1 text-sm font-medium', getChangeColor(stats.total_change_percent))}>
              {getChangeIcon(stats.total_change_percent)}
              <span>{Number(stats.total_change_percent) > 0 ? '+' : ''}{Number(stats.total_change_percent).toFixed(1)}% total</span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t">
          {/* Estadísticas */}
          {stats && stats.total_records > 0 && (
            <div className="bg-blue-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Primer costo</p>
                <p className="font-medium">{stats.first_cost ? formatPrice(stats.first_cost) : '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Último costo</p>
                <p className="font-medium">{stats.last_cost ? formatPrice(stats.last_cost) : '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Costo mínimo</p>
                <p className="font-medium text-green-600">{stats.min_cost ? formatPrice(stats.min_cost) : '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Costo máximo</p>
                <p className="font-medium text-red-600">{stats.max_cost ? formatPrice(stats.max_cost) : '-'}</p>
              </div>
            </div>
          )}

          {/* Lista de cambios */}
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Fecha</th>
                  <th className="text-right px-4 py-2">Costo Ant.</th>
                  <th className="text-right px-4 py-2">Nuevo Costo</th>
                  <th className="text-right px-4 py-2">Cambio</th>
                  <th className="text-left px-4 py-2">Notas</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Calendar className="h-3 w-3" />
                        {formatDate(record.created_at)}
                      </div>
                      {record.user_name && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <User className="h-3 w-3" />
                          {record.user_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">
                      {record.old_cost ? formatPrice(record.old_cost) : '-'}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      {formatPrice(record.new_cost)}
                    </td>
                    <td className={clsx('px-4 py-2 text-right font-medium', getChangeColor(record.cost_change_percent))}>
                      <div className="flex items-center justify-end gap-1">
                        {getChangeIcon(record.cost_change_percent)}
                        {record.cost_change_percent !== null && record.cost_change_percent !== undefined
                          ? `${Number(record.cost_change_percent) > 0 ? '+' : ''}${Number(record.cost_change_percent).toFixed(1)}%`
                          : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-600 max-w-[200px] truncate">
                      {record.supplier && (
                        <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded mr-1">
                          {record.supplier}
                        </span>
                      )}
                      {record.quantity_purchased && (
                        <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded mr-1">
                          x{record.quantity_purchased}
                        </span>
                      )}
                      {record.notes && (
                        <span title={record.notes}>{record.notes}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
